import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { isEmpty } from 'ramda';
import { Icon } from '@modulor/react';
import Local from '../Local/Local';
import { brandSelector } from '../../redux/reducers/app/params';
import { SCCOM_BRAND } from '../../constants/brands';
import { NAMESPACES } from '../../constants/namespaces';
import styles from './BonusNotifications.module.css';
import { LinkToWageringBonus } from './LinkToWageringBonus';

const BonusNotifications = ({ className, bonus, setGTMMessage }) => {
  const { cashbackCollecting, wageringBonus } = bonus;
  const brand = useSelector(brandSelector);
  const isSCCOM = brand === SCCOM_BRAND;

  const notifications = [
    cashbackCollecting?.active
      ? {
          isAvailable: cashbackCollecting?.gameAvailableForCollecting,
          titleTranslationKey: cashbackCollecting?.gameAvailableForCollecting
            ? 'Cashback collecting'
            : 'Cashback not collecting',
          subtitleTranslationKey: cashbackCollecting?.gameAvailableForCollecting
            ? "Don't hesitate"
            : 'Try another game',
        }
      : null,

    wageringBonus?.active
      ? {
          isAvailable:
            wageringBonus?.gameAvailableForWagering ||
            parseInt(wageringBonus?.gameContributionFactor),
          titleTranslationKey: wageringBonus?.gameAvailableForWagering
            ? 'Game for wagering'
            : parseInt(wageringBonus?.gameContributionFactor)
            ? 'Credit to the wagering'
            : 'Bonus not wagering',
          subtitleTranslationKey: wageringBonus?.gameAvailableForWagering
            ? "Don't hesitate"
            : parseInt(wageringBonus?.gameContributionFactor)
            ? 'of your bet'
            : 'Try another game',

          gameContributionFactor: wageringBonus?.gameContributionFactor
            ? `${wageringBonus?.gameContributionFactor}%`
            : null,
          shouldDisplayBonusLink: Boolean(
            wageringBonus?.active && !wageringBonus?.gameContributionFactor
          ),
        }
      : null,
  ].filter(Boolean);

  useEffect(() => {
    setGTMMessage && setGTMMessage(notifications?.[0]?.titleTranslationKey);
  }, [setGTMMessage, notifications]);

  if (isEmpty(notifications)) {
    return null;
  }

  return (
    <ul className={classNames(styles['lc-component'], className)}>
      {notifications.map(
        ({
          isAvailable,
          titleTranslationKey,
          subtitleTranslationKey,
          gameContributionFactor,
          shouldDisplayBonusLink,
        }) => (
          <li key={titleTranslationKey} className={styles['lc-notification']}>
            <AvailabilityIcon
              available={isAvailable}
              className={classNames(
                styles['lc-availabilityIcon'],
                isAvailable && styles.deposit_green,
                {
                  [styles['lc-availabilityIcon_sccom']]: isSCCOM,
                }
              )}
              style={isSCCOM ? {} : { width: 'var(--gap-box-width)' }}
              isSCCOM={isSCCOM}
            />

            <div className={styles['lc-message']}>
              <span className={styles['lc-title']}>
                <Local namespace={NAMESPACES.BONUS}>
                  {titleTranslationKey}
                </Local>
              </span>
              <span className={styles['lc-subtitle']}>
                <Local namespace={NAMESPACES.BONUS}>
                  {subtitleTranslationKey}
                </Local>
              </span>
            </div>

            {gameContributionFactor && (
              <div className={styles['lc-label']}>{gameContributionFactor}</div>
            )}
            {shouldDisplayBonusLink && <LinkToWageringBonus />}
          </li>
        )
      )}
    </ul>
  );
};

BonusNotifications.propTypes = {
  bonus: PropTypes.shape({
    cashbackCollecting: PropTypes.shape({
      active: PropTypes.bool,
      gameAvailableForCollecting: PropTypes.bool,
    }),
    wageringBonus: PropTypes.shape({
      active: PropTypes.bool,
      gameAvailableForWagering: PropTypes.bool,
    }),
  }),
  className: PropTypes.string,
  setGTMMessage: PropTypes.func,
};
BonusNotifications.defaultProps = {
  bonus: {
    cashbackCollecting: {
      active: false,
      gameAvailableForCollecting: false,
    },
    wageringBonus: {
      active: false,
      gameAvailableForWagering: false,
    },
  },
};

export function AvailabilityIcon({ available, isSCCOM, ...props }) {
  return available ? (
    isSCCOM ? (
      <Icon {...props} name="info_circle_outlined" />
    ) : (
      <Icon {...props} name="check_circle_filled" fill="dynamic-green" />
    )
  ) : (
    <Icon {...props} name="error_triangle_filled" fill="icon-warning" />
  );
}

export default BonusNotifications;
